/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/react-in-jsx-scope */
/* eslint-disable react/jsx-curly-brace-presence */
import { ImageIcon } from '@saleshandy/design-system';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Images } from '../../../../../shared/app-constants';
import { OverlayTooltip } from '../../../../../shared/design-system/components/overlay';
import { SearchIcon } from '../../../../../shared/svg';
import { accessibleOnClick } from '../../../../../shared/utils/accessible-on-click';
import { RootState } from '../../../../../store/root-reducer';
import { setShowAiSearchPopup } from '../../../v2-lead-slice';
import { FilterComponentType, LeadFinderSecondaryTab } from '../../../types';
import {
  contactAdmin,
  redirectWithoutRefresh,
  SubscriptionPlans,
} from '../../../../../shared/utils';
import GlobalRestrictionModal from '../../../../home/components/global-restriction-modal';
import { Routes } from '../../../../../shared/routes';
import hasPermission from '../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../shared/utils/access-control/enums/permissions';

type KeywordInputComponentProps = {
  handleQuery?: (value: any) => void;
  keywordInputRef?: React.RefObject<HTMLInputElement>;
  activeSubTab: LeadFinderSecondaryTab;
  hideAISearch?: boolean;
  applyPendingFilters?: (filtersToApply?: URLSearchParams) => void;
};

const KeywordInputComponent: React.FC<KeywordInputComponentProps> = ({
  handleQuery = () => {},
  keywordInputRef,
  activeSubTab,
  hideAISearch = false,
  applyPendingFilters,
}) => {
  // ** REDUX Hooks
  const dispatch = useDispatch();
  const showAiSearchPopup = useSelector(
    (state: RootState) => state?.leadFinderV2?.showAiSearchPopup || false,
  );

  const location = useLocation();

  const queryParams = new URLSearchParams(location?.search);
  const isSavedTab = activeSubTab === LeadFinderSecondaryTab.SAVED;
  const searchKey = isSavedTab ? 'search' : 'keywords';
  const [search, setSearch] = useState(queryParams.get(searchKey) || '');
  const [isRestrictionModalShow, setIsRestrictionModalShow] = useState<boolean>(
    false,
  );
  const isFilterRestrictionEnabled = useSelector(
    (state: RootState) => state?.home?.leadFinder?.isFilterRestrictionEnabled,
  );
  const leadFinderPlanCode = useSelector(
    (state: RootState) => state?.home?.leadFinderSubscription?.planCode,
  );

  const isFreePlanRestrictionEnabled = useMemo(() => (
      isFilterRestrictionEnabled === 1 &&
      leadFinderPlanCode === SubscriptionPlans.LEAD_FINDER_FREE
    ), [isFilterRestrictionEnabled, leadFinderPlanCode]);

  // ** Handlers
  const openAiSearchPopup = useCallback(() => {
    if (isFreePlanRestrictionEnabled) {
      setIsRestrictionModalShow(true);
      return;
    }
    if (!showAiSearchPopup) {
      dispatch(setShowAiSearchPopup(true));
    }
  }, [dispatch, showAiSearchPopup]);

  const handleSearchTermValidationOnEnter = (e) => {
    if (e.keyCode === 13) {
      const trimmedValue = e.target?.value?.trim();

      setSearch(trimmedValue);

      // Trigger search API immediately on Enter press
      if (applyPendingFilters) {
        handleQuery({
          filterKey: searchKey,
          filterValue: trimmedValue,
          filterType: FilterComponentType.SEARCH_INPUT,
          onFiltersUpdated: (updatedFilters) => {
            // Apply the updated filters immediately
            applyPendingFilters(updatedFilters);
          },
        });
      } else {
        handleQuery({
          filterKey: searchKey,
          filterValue: trimmedValue,
          filterType: FilterComponentType.SEARCH_INPUT,
        });
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e?.target?.value);
    if (isSavedTab) {
      return;
    }
    handleQuery({
      filterKey: searchKey,
      filterValue: e?.target?.value,
      filterType: FilterComponentType.SEARCH_INPUT,
    });
  };

  const clearSearch = () => {
    setSearch('');
    if (applyPendingFilters) {
      handleQuery({
        filterKey: searchKey,
        filterValue: '',
        filterType: FilterComponentType.SEARCH_INPUT,
        onFiltersUpdated: (updatedFilters) => {
          applyPendingFilters(updatedFilters);
        },
      });
    } else {
      handleQuery({
        filterKey: searchKey,
        filterValue: '',
        filterType: FilterComponentType.SEARCH_INPUT,
      });
    }
  };

  useEffect(() => {
    setSearch(queryParams.get(searchKey) || '');
  }, [location?.search]);

  return (
    <>
      <div className="w-100">
        <div className="auto-search-container">
          <span className="search-icon">
            <SearchIcon width={16} height={16} />
          </span>

          <input
            ref={keywordInputRef}
            type="text"
            className="auto-search-input-saved"
            onChange={handleInputChange}
            onKeyDown={handleSearchTermValidationOnEnter}
            value={search}
            placeholder="Search a name or keyword"
            maxLength={200}
          />

          {search ? (
            <span onClick={clearSearch} className="clear-icon">
              <i className="sh-close-o" />
            </span>
          ) : (
            activeSubTab !== LeadFinderSecondaryTab.SAVED &&
            !hideAISearch && (
              <OverlayTooltip
                text={
                  <>
                    Search using AI
                    <span className="auto-search-tooltip-beta">Beta</span>
                  </>
                }
                className="auto-search-tooltip"
              >
                <div
                  className="auto-search-shortkey-icon"
                  {...accessibleOnClick(openAiSearchPopup)}
                >
                  <ImageIcon
                    src={Images.Stars}
                    width={16}
                    height={16}
                    alt="stars"
                  />
                </div>
              </OverlayTooltip>
            )
          )}
        </div>
      </div>
      {isRestrictionModalShow && (
        <GlobalRestrictionModal
          show={isRestrictionModalShow}
          onClose={() => setIsRestrictionModalShow(false)}
          title="Upgrade your plan to use AI Search"
          content="Your current plan doesn’t allow you to use AI Search."
          primaryBtnLabel="Upgrade"
          redirectTo={'billing/upgrade-plan/lead-finder'}
          onSubmit={() => {
            if (hasPermission(Permissions.ACCOUNT_SUBSCRIPTION_READ)) {
              redirectWithoutRefresh(
                Routes.Setting_Billing_Upgrade_Lead_Finder_Plan,
              );
            } else {
              contactAdmin(
                'Upgrade request',
                'Please upgrade my account to Pro to unlock premium filters.',
              );
            }
          }}
        />
      )}
    </>
  );
};

export default KeywordInputComponent;
